import { Button, makeStyles, tokens } from '@fluentui/react-components';
import { ChevronDown16Regular, ChevronUp16Regular, Open16Regular } from '@fluentui/react-icons';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { computeCitationView } from '../../utils/ChatMessageUtils';
import { ContentMetadata } from '../../../libs/models/ContentMetadata';
import { SourceFavicon } from './SourceFavicon';
import { getHostname, isSafeUrl, sanitizeTitle } from './sourceUtils';
import { withReferralParams } from '../../../libs/utils/referralTracking';

const useClasses = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
    },
    sourceList: {
        overflow: 'hidden',
        transition: 'max-height 240ms cubic-bezier(0.5, 0, 0.2, 1), opacity 240ms cubic-bezier(0.5, 0, 0.2, 1)',
        maxHeight: '0px',
        opacity: 0,
        width: '100%',
        maxWidth: '460px',
    },
    sourceListOpen: {
        maxHeight: '600px',
        opacity: 1,
        marginTop: tokens.spacingVerticalXS,
        overflowY: 'auto',
    },
    sourceListInner: {
        display: 'flex',
        flexDirection: 'column',
        gap: '0px',
        paddingLeft: tokens.spacingHorizontalS,
        borderLeft: `2px solid ${tokens.colorBrandStroke2}`,
        marginLeft: tokens.spacingHorizontalS,
    },
    sourceRow: {
        display: 'flex',
        alignItems: 'center',
        gap: tokens.spacingHorizontalS,
        paddingTop: tokens.spacingVerticalXS,
        paddingBottom: tokens.spacingVerticalXS,
        paddingLeft: tokens.spacingHorizontalS,
        paddingRight: tokens.spacingHorizontalS,
        borderRadius: tokens.borderRadiusSmall,
        textDecoration: 'none',
        color: tokens.colorNeutralForeground1,
        ':hover': {
            backgroundColor: tokens.colorNeutralBackground1Hover,
            textDecoration: 'none',
        },
    },
    sourceMeta: {
        display: 'flex',
        alignItems: 'baseline',
        gap: tokens.spacingHorizontalS,
        minWidth: '0',
        overflow: 'hidden',
    },
    sourceTitle: {
        fontSize: tokens.fontSizeBase200,
        fontWeight: tokens.fontWeightMedium,
        color: tokens.colorNeutralForeground1,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    sourceHost: {
        fontSize: tokens.fontSizeBase100,
        color: tokens.colorNeutralForeground3,
        whiteSpace: 'nowrap',
    },
    externalIcon: {
        marginLeft: 'auto',
        color: tokens.colorNeutralForeground3,
        opacity: 0,
        transition: 'opacity 160ms',
        flexShrink: 0,
    },
    sourceRowHoverIcon: {
        ':hover .sources-ext-icon': {
            opacity: 1,
        },
    },
});

interface SourcesDropdownProps {
    contentMetadata: ContentMetadata[] | undefined;
    isChatResponseComplete: boolean;
    isLastMessage: boolean;
}

export const SourcesDropdown: React.FC<SourcesDropdownProps> = ({
    contentMetadata,
    isChatResponseComplete,
    isLastMessage,
}) => {
    const classes = useClasses();
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);

    const visibleSources = useMemo(() => computeCitationView(contentMetadata).visibleEntries, [contentMetadata]);

    if (visibleSources.length === 0) {
        return null;
    }

    if (!isChatResponseComplete && isLastMessage) {
        return null;
    }

    return (
        <div className={classes.root}>
            <Button
                appearance="subtle"
                size="small"
                icon={open ? <ChevronUp16Regular /> : <ChevronDown16Regular />}
                iconPosition="after"
                onClick={() => {
                    setOpen((o) => !o);
                }}
                aria-expanded={open}
            >
                {t('chathistorysourcelinks.references')} ({visibleSources.length})
            </Button>
            {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
            <div
                className={`${classes.sourceList} ${open ? classes.sourceListOpen : ''}`}
                aria-hidden={!open}
                {...(!open ? ({ inert: '' } as any) : {})}
            >
                <div className={classes.sourceListInner}>
                    {visibleSources.map((source) => {
                        const hostname = source.url ? getHostname(source.url) : '';
                        return isSafeUrl(source.url) ? (
                            <a
                                key={source.displayOrder}
                                className={`${classes.sourceRow} ${classes.sourceRowHoverIcon}`}
                                href={withReferralParams(source.url, 'citation')}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <SourceFavicon hostname={hostname} variant="dropdown" />
                                <div className={classes.sourceMeta}>
                                    <span
                                        className={classes.sourceTitle}
                                        dangerouslySetInnerHTML={{ __html: sanitizeTitle(source.title) }}
                                    />
                                    <span className={classes.sourceHost}>{hostname}</span>
                                </div>
                                <Open16Regular className={`${classes.externalIcon} sources-ext-icon`} />
                            </a>
                        ) : (
                            <div key={source.displayOrder} className={classes.sourceRow}>
                                <SourceFavicon hostname={hostname} variant="dropdown" />
                                <div className={classes.sourceMeta}>
                                    <span
                                        className={classes.sourceTitle}
                                        dangerouslySetInnerHTML={{ __html: sanitizeTitle(source.title) }}
                                    />
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};
